// =============================================
// UploadQuestionsPage.jsx  (UPDATED - JAMB + POST-UTME UPLOAD SUPPORT)
// =============================================

import { useEffect, useMemo, useState } from "react";
import { validateLatex } from "../../utils/latexValidator";

import {
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
 
  Chip,


} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";



import api from "../../utils/api";
import ImageUploadField from "../../components/questions/ImageUploadField";

// ===============================
// ✅ NEW: Upload Modes
// ===============================
const QUESTION_TYPES = [
  { value: "JAMB", label: "JAMB (Subject Practice)" },
  { value: "POST_UTME", label: "Post-UTME (Screening Mode)" },
];

// Post-UTME screening buckets (what it tests)
const POST_UTME_BUCKETS = [
  { value: "ENG", label: "ENG — English" },
  { value: "MTH", label: "MTH — Mathematics" },
  { value: "IQ", label: "IQ — Reasoning/Aptitude" },
  { value: "CA", label: "CA — Current Affairs" },
];

// Helpers to avoid mistakes
const getExamStyle = (questionType) => (questionType === "POST_UTME" ? "SCREENING" : "JAMB");
const getTags = (questionType) => (questionType === "POST_UTME" ? ["POST_UTME"] : []);

const fileToDataUrl = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

// ---------------------------------------------
// Subject Select dropdown
// ---------------------------------------------
function SubjectSelect({ value, onChange, label = "Subject", fullWidth = true, onLoaded }) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get("/admin/subjects")
      .then((r) => {
        if (!mounted) return;
        const arr = (r.data?.subjects || r.data || []).map((s) => ({
          id: s.id,
          name: s.name,
          code: s.code?.toUpperCase() || "",
          status: s.status !== false,
        }));
        setOptions(arr);
        onLoaded?.(arr);
      })
      .finally(() => setLoading(false));
    return () => (mounted = false);
  }, []);

  return (
    <TextField
      select
      label={label}
      value={value || ""}
      onChange={(e) => onChange?.(e.target.value)}
      fullWidth={fullWidth}
      required
      disabled={loading}
    >
      {options.map((s) => (
        <MenuItem key={s.id} value={s.id}>
          {s.name} {s.code ? `(${s.code})` : ""}
        </MenuItem>
      ))}
    </TextField>
  );
}


// =====================================================
// SINGLE QUESTION FORM
// =====================================================
function SingleQuestionForm() {
  const [questionType, setQuestionType] = useState("JAMB"); // ✅ NEW
  const [bucketCode, setBucketCode] = useState(""); // ✅ NEW (Post-UTME only)

  const [subjectId, setSubjectId] = useState("");
  const [form, setForm] = useState({
    question: "",
    option_a: "",
    option_b: "",
    option_c: "",
    option_d: "",
    correct_option: "A",
    explanation: "",
    difficulty: "medium",
    stem_image_url: "",
  });

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "info" });

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const isValid = useMemo(() => {
    if (!questionType) return false;
  if (questionType === "JAMB" && !subjectId) return false;


    // Post-UTME requires bucket_code (ENG/MTH/IQ/CA)
    if (questionType === "POST_UTME" && !bucketCode) return false;

    if (!form.question?.trim()) return false;
    if (!form.option_a?.trim() || !form.option_b?.trim() || !form.option_c?.trim() || !form.option_d?.trim())
      return false;
    if (!"ABCD".includes(form.correct_option)) return false;
    return true;
  }, [form, subjectId, questionType, bucketCode]);

  const submit = async (e) => {
    e?.preventDefault?.();

    if (!isValid) {
      setToast({ open: true, msg: "Fill all required fields.", severity: "error" });
      return;
    }

    // ===============================
    // 🛑 LaTeX VALIDATION (EXISTING)
    // ===============================
    const latexFields = [
      { label: "Question", value: form.question },
      { label: "Option A", value: form.option_a },
      { label: "Option B", value: form.option_b },
      { label: "Option C", value: form.option_c },
      { label: "Option D", value: form.option_d },
      { label: "Explanation", value: form.explanation },
    ];

    for (const field of latexFields) {
      const errors = validateLatex(field.value);
      if (errors) {
        setToast({
          open: true,
          msg: `LaTeX error in ${field.label}:\n` + errors.map((e) => `• ${e}`).join("\n"),
          severity: "error",
        });
        return; // 🚫 BLOCK SAVE
      }
    }

    const payload = {
      subject_id: subjectId,
      question: form.question,
      option_a: form.option_a,
      option_b: form.option_b,
      option_c: form.option_c,
      option_d: form.option_d,
      correct_option: form.correct_option,
      explanation: form.explanation,
      difficulty: form.difficulty,
      stem_image_url: form.stem_image_url || null,

      // ✅ NEW FIELDS (backend should accept them; harmless if ignored)
      exam_style: getExamStyle(questionType), // JAMB | SCREENING
      bucket_code: questionType === "POST_UTME" ? bucketCode : null, // ENG/MTH/IQ/CA
      tags: getTags(questionType), // ["POST_UTME"] or []
    };

    try {
      setSaving(true);
      await api.post("/admin/questions", payload);
      setToast({ open: true, msg: "Saved successfully", severity: "success" });

      // reset form
      setForm({
        question: "",
        option_a: "",
        option_b: "",
        option_c: "",
        option_d: "",
        correct_option: "A",
        explanation: "",
        difficulty: "medium",
        stem_image_url: "",
      });

      // keep questionType + subjectId; reset bucket only for Post-UTME
      if (questionType === "POST_UTME") setBucketCode("");
    } catch (e) {
      setToast({
        open: true,
        msg: e?.response?.data?.message || "Failed to save.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
      <Stack spacing={2} onSubmit={submit} component="form">
        {/* ✅ NEW: Question Type */}
        <TextField
          select
          label="Question Type"
          value={questionType}
          onChange={(e) => {
            const v = e.target.value;
            setQuestionType(v);
            if (v !== "POST_UTME") setBucketCode("");
          }}
          fullWidth
          required
        >
          {QUESTION_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>

        {/* Subject is still required (you said Post-UTME ENG/MTH will be separate subjects) */}
       {questionType === "JAMB" && (
  <SubjectSelect value={subjectId} onChange={setSubjectId} />
)}


        {/* ✅ NEW: Bucket Code only for Post-UTME */}
        {questionType === "POST_UTME" && (
          <TextField
            select
            label="Post-UTME Bucket (Required)"
            value={bucketCode}
            onChange={(e) => setBucketCode(e.target.value)}
            fullWidth
            required
            helperText="Choose what this question tests (ENG/MTH/IQ/CA). Exam style is auto-set to SCREENING and tagged POST_UTME."
          >
            {POST_UTME_BUCKETS.map((b) => (
              <MenuItem key={b.value} value={b.value}>
                {b.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* Quick badge to show what will be stored */}
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          <Chip label={`exam_style: ${getExamStyle(questionType)}`} />
          {questionType === "POST_UTME" && <Chip label="tag: POST_UTME" color="primary" />}
          {questionType === "POST_UTME" && bucketCode && <Chip label={`bucket_code: ${bucketCode}`} />}
        </Stack>

        <TextField
          label="Question"
          value={form.question}
          onChange={(e) => set({ question: e.target.value })}
          multiline
          minRows={3}
          fullWidth
        />

        <ImageUploadField
          label="Question Image (optional)"
          value={form.stem_image_url}
          onChange={(url) => set({ stem_image_url: url })}
        />

        <Divider />

        <Typography variant="subtitle2">Options (mark one correct)</Typography>

        <Stack direction="row" spacing={2}>
          {["A", "B", "C", "D"].map((l) => (
            <Chip
              key={l}
              label={`Correct: ${l}`}
              color={form.correct_option === l ? "primary" : "default"}
              onClick={() => set({ correct_option: l })}
            />
          ))}
        </Stack>

        {["A", "B", "C", "D"].map((l) => (
          <TextField
            key={l}
            label={`Option ${l}`}
            value={form[`option_${l.toLowerCase()}`]}
            onChange={(e) => set({ [`option_${l.toLowerCase()}`]: e.target.value })}
            fullWidth
          />
        ))}

        <TextField
          label="Explanation (optional)"
          value={form.explanation}
          onChange={(e) => set({ explanation: e.target.value })}
          multiline
          minRows={2}
        />

        <TextField
          select
          label="Difficulty"
          value={form.difficulty}
          onChange={(e) => set({ difficulty: e.target.value })}
          sx={{ width: 200 }}
        >
          <MenuItem value="easy">Easy</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="hard">Hard</MenuItem>
        </TextField>

        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={() => window.history.back()}>
            Cancel
          </Button>

          <Button type="submit" variant="contained" startIcon={<CheckCircleIcon />} disabled={!isValid || saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </Stack>
      </Stack>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
        <Alert severity={toast.severity}>{toast.msg}</Alert>
      </Snackbar>
    </Paper>
  );
}

// =====================================================
// BULK UPLOAD PANEL
// =====================================================
function BulkUploadPanel() {
  const [questionType, setQuestionType] = useState("JAMB"); // ✅ NEW
  const [bucketCode, setBucketCode] = useState(""); // ✅ NEW (Post-UTME only)

  const [subjectId, setSubjectId] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [preview, setPreview] = useState([]);

  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "info" });

  const selected = useMemo(() => subjects.find((s) => s.id === subjectId) || null, [subjects, subjectId]);

  const pickCsv = (e) => {
    const f = e.target.files?.[0];
    setCsvFile(f || null);
    if (f) {
      f.text().then((txt) => setPreview(txt.split(/\r?\n/).slice(0, 6)));
    }
    e.target.value = "";
  };

  const pickZip = (e) => {
    const f = e.target.files?.[0];
    setZipFile(f || null);
    e.target.value = "";
  };

  const uploadNow = async () => {
    if (!questionType) {
      setToast({ open: true, msg: "Select Question Type", severity: "error" });
      return;
    }
   if (questionType === "JAMB" && !subjectId) {
  setToast({ open: true, msg: "Select a subject", severity: "error" });
  return;
}

    if (questionType === "POST_UTME" && !bucketCode) {
      setToast({ open: true, msg: "Select Post-UTME Bucket (ENG/MTH/IQ/CA)", severity: "error" });
      return;
    }
    if (!csvFile) {
      setToast({ open: true, msg: "Select a CSV file", severity: "error" });
      return;
    }

    // ===============================
    // 🛑 CSV LaTeX PREFLIGHT CHECK (EXISTING)
    // ===============================
    if (csvFile) {
      const text = await csvFile.text();
      const lines = text.split(/\r?\n/).slice(1); // skip header

      for (let i = 0; i < lines.length; i++) {
        const cols = lines[i].split(",");
        if (cols.length < 5) continue;

        const fieldsToCheck = [
          { label: "Question", value: cols[0] },
          { label: "Option A", value: cols[1] },
          { label: "Option B", value: cols[2] },
          { label: "Option C", value: cols[3] },
          { label: "Option D", value: cols[4] },
        ];

        for (const field of fieldsToCheck) {
          const errors = validateLatex(field.value);
          if (errors) {
            setToast({
              open: true,
              msg: `CSV LaTeX error (line ${i + 2}) in ${field.label}:\n` + errors.map((e) => `• ${e}`).join("\n"),
              severity: "error",
            });
            return; // 🚫 BLOCK UPLOAD
          }
        }
      }
    }

    try {
      setBusy(true);
      const fd = new FormData();
      fd.append("subject_id", subjectId);
      fd.append("file", csvFile);
      if (zipFile) fd.append("images_zip", zipFile);

      // ✅ NEW: metadata for backend validation/insertion
      fd.append("exam_style", getExamStyle(questionType)); // JAMB | SCREENING
      fd.append("question_type", questionType); // optional helper
      fd.append("tags", JSON.stringify(getTags(questionType))); // ["POST_UTME"] or []
      if (questionType === "POST_UTME") {
        fd.append("bucket_code", bucketCode); // ENG/MTH/IQ/CA
      }

      const r = await api.post("/admin/questions/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setToast({
        open: true,
        msg: r.data?.message || "Upload completed",
        severity: "success",
      });

      setCsvFile(null);
      setZipFile(null);
      setPreview([]);

      if (questionType === "POST_UTME") setBucketCode("");
    } catch (e) {
      setToast({
        open: true,
        msg: e?.response?.data?.message || "Upload failed",
        severity: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
      <Stack spacing={2}>
        {/* ✅ NEW: Question Type */}
        <TextField
          select
          label="Question Type"
          value={questionType}
          onChange={(e) => {
            const v = e.target.value;
            setQuestionType(v);
            if (v !== "POST_UTME") setBucketCode("");
          }}
          fullWidth
          required
        >
          {QUESTION_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>

      {questionType === "JAMB" && (
  <SubjectSelect
    value={subjectId}
    onChange={setSubjectId}
    onLoaded={setSubjects}
  />
)}


        {/* ✅ NEW: Bucket Code only for Post-UTME bulk (upload per bucket) */}
        {questionType === "POST_UTME" && (
          <TextField
            select
            label="Post-UTME Bucket for this CSV (Required)"
            value={bucketCode}
            onChange={(e) => setBucketCode(e.target.value)}
            fullWidth
            required
            helperText="Bulk CSV should be uploaded per bucket (ENG/MTH/IQ/CA). Exam style will be SCREENING, tagged POST_UTME."
          >
            {POST_UTME_BUCKETS.map((b) => (
              <MenuItem key={b.value} value={b.value}>
                {b.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          <Chip label={`exam_style: ${getExamStyle(questionType)}`} />
          {questionType === "POST_UTME" && <Chip label="tag: POST_UTME" color="primary" />}
          {questionType === "POST_UTME" && bucketCode && <Chip label={`bucket_code: ${bucketCode}`} />}
        </Stack>

        <Stack direction="row" spacing={2}>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
            Choose CSV
            <input hidden type="file" accept=".csv" onChange={pickCsv} />
          </Button>

          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
            Optional ZIP
            <input hidden type="file" accept=".zip" onChange={pickZip} />
          </Button>

          <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={uploadNow} disabled={busy}>
            {busy ? "Uploading..." : "Upload"}
          </Button>
        </Stack>

        {preview.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafafa" }}>
            <Typography variant="caption">Preview (first lines)</Typography>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{preview.join("\n")}</pre>
          </Paper>
        )}
      </Stack>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
        <Alert severity={toast.severity}>{toast.msg}</Alert>
      </Snackbar>
    </Paper>
  );
}

// =====================================================
// MAIN PAGE
// =====================================================
export default function UploadQuestionsPage() {
  const [tab, setTab] = useState(1);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700}>
        Upload Questions
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Upload using CSV (single subject) or manually add single questions.
      </Typography>

      <Paper variant="outlined" sx={{ mt: 2, borderRadius: 3 }}>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="fullWidth">
          <Tab label="Single Question" />
          <Tab label="Bulk Upload (CSV/ZIP)" />
        </Tabs>
        <Divider />
        <Box sx={{ p: 2 }}>
          {tab === 0 && <SingleQuestionForm />}
          {tab === 1 && <BulkUploadPanel />}
        </Box>
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Alert severity="info">
          CSV Header:{" "}
          <b>
            question, option_a, option_b, option_c, option_d, correct_option, explanation, difficulty, stem_image_url
          </b>
          <br />
          <Typography variant="caption" component="div">
            Note: For Post-UTME bulk upload, <b>bucket_code</b>, <b>exam_style</b> and <b>tags</b> are set by the UI (not inside the CSV).
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
}
